/**
 * 読み上げパネル（M11 手順 2）。再生／停止・速さ・音声の 3 つだけを持つ。
 *
 * 設計の要点:
 * - **音声の選択は必須**（DECISIONS 2026-08-15）。スクリーンリーダー利用者は自分の道具の音声を
 *   随時変えられるが、製品の読み上げを聴く利用者は**製品が用意した口しか持たない**。
 * - **速さはスライダにしない。** `<input type="range">` は経路をなぞる操作（`2.5.7`）で、
 *   単一ポインタの代替を別に用意する羽目になる。段階を選ぶだけなら `<select>` で足りる。
 * - 再生と停止は**1 つのボタン**が状態で切り替わる。名前も `aria-pressed` も状態に追随させる
 *   （`4.1.2` — 名前・役割・状態）。
 * - 文言はすべて `textContent`（利用者辞書が HTML を持ち込んでも実行されない）。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { Translate } from '../i18n/i18n';
import type { PanelContent } from './panels';
import type { SpeechReader, SpeechState, VoiceLike } from './speech-reader';

/** 選べる速さ。等倍を真ん中に置く。 */
const RATES = [0.75, 1, 1.25, 1.5, 2] as const;

export interface SpeechSource {
  /** 表示中ページの本文（区切り列）。空なら読み上げるものが無い。 */
  segments(): readonly { text: string; lang?: string }[];
  /** 誌面本文の言語（区切りに `lang` が無いときの既定）。 */
  lang(): string;
}

export class SpeechPanel implements PanelContent {
  readonly el: HTMLElement;
  private readonly toggle: HTMLButtonElement;
  private readonly rate: HTMLSelectElement;
  private readonly voice: HTMLSelectElement;
  /**
   * 縮退の理由を**文字で**出す場所（DECISIONS 2026-08-15 —「文字で出す・自動では喋らない」）。
   * 音声で案内する案は成立しない: その言語の声が無いから困っているのに、その言語で喋れない。
   * 焦点が動かないまま内容が変わる（`voiceschanged` で遅れて届く）のでライブ領域にする（`4.1.3`）。
   */
  private readonly status: HTMLParagraphElement;

  constructor(
    private readonly doc: Document,
    registry: DisposeRegistry,
    private readonly translate: Translate,
    private readonly reader: SpeechReader,
    private readonly source: SpeechSource,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('speech');

    this.toggle = doc.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = `${cls('button')} ${cls('speech-toggle')}`;
    registry.listen(this.toggle, 'click', () => this.onToggle());

    this.rate = this.select('speech-rate', translate('speech.rate'));
    for (const value of RATES) {
      const option = doc.createElement('option');
      option.value = String(value);
      option.textContent = `${value}x`;
      if (value === 1) option.selected = true;
      this.rate.appendChild(option);
    }

    this.voice = this.select('speech-voice', translate('speech.voice'));
    this.fillVoices();
    // 一覧は遅れて来ることがある（`voiceschanged`）。来たら選び直せるように作り直す。
    reader.watchVoices(() => {
      this.fillVoices();
      // 一覧は遅れて来る。届いた時点で「使えない」から「使える」へ戻せるようにする。
      this.applyAvailability();
    });
    reader.watchState((state) => this.applyState(state));

    this.status = doc.createElement('p');
    this.status.className = cls('speech-status');
    // 家の作法に合わせて aria-live で持つ（検索・コメントのパネルと同じ形）。
    this.status.setAttribute('aria-live', 'polite');

    this.el.append(this.field(translate('speech.rate'), this.rate));
    this.el.append(this.field(translate('speech.voice'), this.voice));
    this.el.append(this.toggle);
    this.el.append(this.status);
    this.applyState(reader.current());
    this.applyAvailability();
  }

  /** パネルを閉じても読み上げは止めない（別のページを見ながら聴ける）。破棄は Shell が行う。 */
  private onToggle(): void {
    if (this.reader.current() === 'speaking') {
      this.reader.stop();
      return;
    }
    const segments = this.source.segments();
    if (segments.length === 0) {
      // 黙って何も起きないのが最悪。理由を文字で出す。
      this.status.textContent = this.translate('speech.empty');
      return;
    }
    this.status.textContent = '';
    this.reader.speak(segments, {
      lang: this.source.lang(),
      rate: Number(this.rate.value),
      voiceURI: this.voice.value === '' ? null : this.voice.value,
    });
  }

  /** 名前と押下状態を状態に追随させる（`4.1.2`）。 */
  private applyState(state: SpeechState): void {
    const speaking = state === 'speaking';
    this.toggle.textContent = this.translate(speaking ? 'speech.stop' : 'speech.play');
    this.toggle.setAttribute('aria-pressed', speaking ? 'true' : 'false');
  }

  /**
   * 読み上げが使える状態かを反映する。
   *
   * - 音声が 1 つも無い → **使えない**（ボタンを無効にし、理由を文字で出す）
   * - 本の言語に合う音声が無い → 使えるが、既定の音声で読むことを断る
   * - それ以外 → 何も出さない（余計な文言を足さない）
   */
  private applyAvailability(): void {
    const voices = this.reader.voices();
    const lang = this.source.lang();
    this.toggle.disabled = voices.length === 0;

    if (voices.length === 0) {
      this.status.textContent = this.translate('speech.none');
      return;
    }
    const matched = voices.some(
      (voice) => voice.lang.toLowerCase().split('-')[0] === lang.toLowerCase().split('-')[0],
    );
    this.status.textContent = matched ? '' : this.translate('speech.otherLang', { lang });
  }

  private fillVoices(): void {
    const previous = this.voice.value;
    this.voice.textContent = '';
    this.voice.appendChild(this.option('', this.translate('speech.voice.auto')));
    for (const voice of this.reader.voices()) {
      this.voice.appendChild(this.option(voice.voiceURI, this.label(voice)));
    }
    // 選んでいた音声が一覧から消えたらブラウザ既定へ戻す（存在しない口を指したままにしない）。
    this.voice.value = Array.from(this.voice.options).some((o) => o.value === previous)
      ? previous
      : '';
  }

  private label(voice: VoiceLike): string {
    return voice.lang === '' ? voice.name : `${voice.name}（${voice.lang}）`;
  }

  private option(value: string, text: string): HTMLOptionElement {
    const option = this.doc.createElement('option');
    option.value = value;
    option.textContent = text;
    return option;
  }

  private select(name: string, label: string): HTMLSelectElement {
    const select = this.doc.createElement('select');
    select.className = cls(name);
    select.setAttribute('aria-label', label);
    return select;
  }

  /** 可視ラベルつきの 1 行（`<label>` で包むので `id` は要らない）。 */
  private field(text: string, control: HTMLElement): HTMLLabelElement {
    const label = this.doc.createElement('label');
    label.className = cls('speech-field');
    const span = this.doc.createElement('span');
    span.textContent = text;
    label.append(span, control);
    return label;
  }
}
